import { useState } from 'react'
import { AlertTriangle, Ban, CheckCircle2, Download, FileCheck2, FileMinus, FileX2, Loader2, RefreshCw, RotateCcw, Send, XCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { formatCurrency } from '@/lib/utils'
import { esAnulable, etiquetaComprobante, type FacturaResumen } from '@/services/facturasService'

/** Estado de la factura de un cobro, junto al tipo del movimiento (ORR-255). */
export function FacturaBadge({ factura }: { factura?: FacturaResumen }) {
  if (!factura) return null
  const etiqueta = etiquetaComprobante(factura)
  const base = 'gap-1 font-normal'
  const contenido = (() => {
    if (factura.anulada_at) {
      return (
        <Badge variant="outline" className={`${base} text-muted-foreground line-through decoration-1`}>
          <FileX2 className="h-3 w-3" />
          {etiqueta ?? 'Factura'}
        </Badge>
      )
    }
    switch (factura.estado) {
      case 'autorizada':
        return (
          <Badge variant="outline" className={`${base} text-green-700 border-green-300 dark:text-green-400`}>
            <CheckCircle2 className="h-3 w-3" />
            {etiqueta ?? 'Facturado'}
          </Badge>
        )
      case 'pendiente':
      case 'enviada':
        return (
          <Badge variant="outline" className={`${base} text-muted-foreground`}>
            <Loader2 className="h-3 w-3 animate-spin" />
            Facturando
          </Badge>
        )
      case 'rechazada':
        return (
          <Badge variant="outline" className={`${base} text-red-700 border-red-300 dark:text-red-400`}>
            <XCircle className="h-3 w-3" />
            Factura rechazada
          </Badge>
        )
      case 'revisar':
        return (
          <Badge variant="outline" className={`${base} text-amber-700 border-amber-300 dark:text-amber-400`}>
            <AlertTriangle className="h-3 w-3" />
            Factura a revisar
          </Badge>
        )
    }
  })()
  const detalle = factura.anulada_at
    ? factura.nc_que_anula
      ? `Anulada con la ${factura.nc_que_anula.replace(/^Nota/, 'nota')}. No se puede reenviar: mandá la nota de crédito.`
      : 'Anulada con nota de crédito. No se puede reenviar: mandá la nota de crédito.'
    : factura.estado === 'autorizada'
      ? `CAE ${factura.cae ?? '-'} · ${formatCurrency(Number(factura.importe_total))}${factura.email_enviado_at ? ' · enviada por mail' : ''}`
      : factura.estado === 'revisar'
        ? 'ARCA no resolvió la emisión. No la vuelvas a emitir: la revisa el operador.'
        : factura.estado === 'rechazada'
          ? factura.error_mensaje || 'ARCA rechazó la factura.'
          : 'La factura se está emitiendo en ARCA.'
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help">{contenido}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{detalle}</TooltipContent>
    </Tooltip>
  )
}

export type AccionDeFactura = 'reintentar' | 'reconsultar' | 'enviarEmail' | 'pdf' | 'anular' | 'acreditar'

interface FacturaMenuItemsProps {
  factura?: FacturaResumen
  puedeFacturar: boolean
  /** Tiene `anularFactura`: anula, o acredita una parte con una nota de crédito parcial. */
  puedeAnular?: boolean
  /** Tiene `eliminarMovimiento`: anular la factura de un cobro también borra el cobro (ORR-255). */
  puedeEliminarCobro?: boolean
  /** Sin él no se ofrece emitir: en la página de Facturas una factura no sale de un cobro. */
  onEmitir?: () => void
  onAccion: (accion: AccionDeFactura, factura: FacturaResumen) => void
}

/** Acciones de una factura: en el menú del cobro y en la página de Facturas. */
export function FacturaMenuItems({
  factura,
  puedeFacturar,
  puedeAnular = false,
  puedeEliminarCobro = false,
  onEmitir,
  onAccion,
}: FacturaMenuItemsProps) {
  const emitir = onEmitir && puedeFacturar && (!factura || factura.anulada_at) && (
    <DropdownMenuItem onClick={onEmitir}>
      <FileCheck2 className="mr-2 h-4 w-4" />
      {factura ? 'Emitir otra factura' : 'Emitir factura'}
    </DropdownMenuItem>
  )
  if (!factura) {
    if (!emitir) return null
    return (
      <>
        <DropdownMenuSeparator />
        {emitir}
      </>
    )
  }
  const doc = factura.clase === 'nc' ? 'nota de crédito' : 'factura'
  return (
    <>
      <DropdownMenuSeparator />
      {emitir}
      {factura.estado === 'autorizada' && (
        <DropdownMenuItem onClick={() => onAccion('pdf', factura)}>
          <Download className="mr-2 h-4 w-4" />
          Descargar {doc}
        </DropdownMenuItem>
      )}
      {factura.estado === 'autorizada' && !factura.anulada_at && puedeFacturar && (
        <DropdownMenuItem onClick={() => onAccion('enviarEmail', factura)}>
          <Send className="mr-2 h-4 w-4" />
          Enviar {doc} por email
        </DropdownMenuItem>
      )}
      {esAnulable(factura) && puedeAnular && (!factura.movimiento_id || puedeEliminarCobro) && (
        <DropdownMenuItem onClick={() => onAccion('anular', factura)} className="text-destructive focus:text-destructive">
          <Ban className="mr-2 h-4 w-4" />
          Anular con nota de crédito
        </DropdownMenuItem>
      )}
      {esAnulable(factura) && puedeAnular && (
        <DropdownMenuItem onClick={() => onAccion('acreditar', factura)}>
          <FileMinus className="mr-2 h-4 w-4" />
          Nota de crédito parcial
        </DropdownMenuItem>
      )}
      {factura.estado === 'rechazada' && puedeFacturar && (
        <DropdownMenuItem onClick={() => onAccion('reintentar', factura)}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Reintentar {doc}
        </DropdownMenuItem>
      )}
      {(factura.estado === 'pendiente' || factura.estado === 'enviada') && (
        <DropdownMenuItem onClick={() => onAccion('reconsultar', factura)}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Actualizar estado de la {doc}
        </DropdownMenuItem>
      )}
    </>
  )
}

interface ConfirmarFacturaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clienteNombre: string
  monto: number
  onConfirmar: (enviarEmail: boolean) => void
}

/** Confirmación antes de emitir: una factura autorizada no se borra, se anula con nota de crédito. */
export function ConfirmarFacturaDialog({ open, onOpenChange, clienteNombre, monto, onConfirmar }: ConfirmarFacturaDialogProps) {
  const [enviarEmail, setEnviarEmail] = useState(true)
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Emitir la factura?</AlertDialogTitle>
          <AlertDialogDescription>
            Se emite ante ARCA una factura a {clienteNombre} por {formatCurrency(monto)}. Una vez emitida no se puede
            borrar: se anula con una nota de crédito.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex items-center gap-2">
          <Checkbox id="factura-enviar-email" checked={enviarEmail} onCheckedChange={(v) => setEnviarEmail(v === true)} />
          <label htmlFor="factura-enviar-email" className="text-sm">
            Enviarla por mail al cliente
          </label>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirmar(enviarEmail)}>Emitir factura</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

interface ConfirmarAnulacionDialogProps {
  factura: FacturaResumen | null
  /** Lo que ya acreditan sus notas de crédito parciales: la anulación acredita el resto. */
  acreditado?: number
  onOpenChange: (open: boolean) => void
  onConfirmar: (enviarEmail: boolean) => void
}

/** Confirmación antes de anular: la nota de crédito también se emite ante ARCA y no se deshace. */
export function ConfirmarAnulacionDialog({ factura, acreditado = 0, onOpenChange, onConfirmar }: ConfirmarAnulacionDialogProps) {
  const [enviarEmail, setEnviarEmail] = useState(true)
  const etiqueta = factura ? etiquetaComprobante(factura) ?? 'la factura' : ''
  const importe = Math.round((Number(factura?.importe_total ?? 0) - acreditado) * 100) / 100
  return (
    <AlertDialog open={!!factura} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Anular la factura?</AlertDialogTitle>
          <AlertDialogDescription>
            Se emite ante ARCA una nota de crédito por {formatCurrency(importe)} que anula la{' '}
            {etiqueta}
            {factura?.receptor_nombre ? ` de ${factura.receptor_nombre}` : ''}
            {acreditado > 0 ? `, por lo que no acreditaron sus notas de crédito parciales (${formatCurrency(acreditado)})` : ''}.
            La nota de crédito tampoco se puede borrar.
            {factura?.movimiento_id &&
              ' Cuando ARCA la autorice, también se elimina el cobro de la cuenta corriente y las deudas que pagaba vuelven a quedar pendientes.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {factura?.receptor_email && (
          <div className="flex items-center gap-2">
            <Checkbox id="nc-enviar-email" checked={enviarEmail} onCheckedChange={(v) => setEnviarEmail(v === true)} />
            <label htmlFor="nc-enviar-email" className="text-sm">
              Enviarla por mail a {factura.receptor_email}
            </label>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirmar(enviarEmail && !!factura?.receptor_email)}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Anular con nota de crédito
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
